import type { DatosDeProyecto, Proyecto } from '@/api/proyectos.api';

/** Ayudantes de la pantalla de proyectos (tanda 3, Ola D). PUROS. */

/** «Vence en 12 días», «Vence hoy», «Venció hace 5 días». */
export function venceEnTexto(dias: number): string {
  if (dias < -1) return `Venció hace ${-dias} días`;
  if (dias === -1) return 'Venció ayer';
  if (dias === 0) return 'Vence hoy';
  if (dias === 1) return 'Vence mañana';
  return `Vence en ${dias} días`;
}

export type Urgencia = 'vencido' | 'pronto' | 'bien';

/** Rojo si ya venció; ámbar si vence en 7 días o menos, para que el dueño actualice el precio a tiempo. */
export function urgencia(dias: number): Urgencia {
  return dias < 0 ? 'vencido' : dias <= 7 ? 'pronto' : 'bien';
}

/** El formulario guarda texto; `aDatos` lo convierte y lo valida. */
export interface Formulario {
  nombre: string;
  zona: string;
  precioDesdeUsd: string;
  inicialMinimaPct: string;
  entrega: string;
  /** Una por línea. */
  amenidades: string;
  vigenteHasta: string;
  noRecomendar: boolean;
  fotos: string[];
  /** Tanda 5: claves de los videos subidos. */
  videos: string[];
  /** Tanda 5: su página en la web del dueño. Vacío = sin enlace. */
  enlace: string;
}

/** Tantos videos acepta la API por proyecto. */
export const MAX_VIDEOS = 10;

export const FORMULARIO_VACIO: Formulario = {
  nombre: '',
  zona: '',
  precioDesdeUsd: '',
  inicialMinimaPct: '',
  entrega: '',
  amenidades: '',
  vigenteHasta: '',
  noRecomendar: false,
  fotos: [],
  videos: [],
  enlace: '',
};

export function aFormulario(p: Proyecto): Formulario {
  return {
    nombre: p.nombre,
    zona: p.zona,
    precioDesdeUsd: String(p.precioDesdeUsd),
    inicialMinimaPct: p.inicialMinimaPct == null ? '' : String(p.inicialMinimaPct),
    entrega: p.entrega ?? '',
    amenidades: p.amenidades.join('\n'),
    vigenteHasta: p.vigenteHasta,
    noRecomendar: p.noRecomendar,
    fotos: p.fotos.map((f) => f.key),
    videos: p.videos.map((v) => v.key),
    enlace: p.enlace ?? '',
  };
}

export function aDatos(f: Formulario): { datos: DatosDeProyecto } | { error: string } {
  if (!f.nombre.trim()) return { error: 'Falta el nombre del proyecto.' };
  if (!f.zona.trim()) return { error: 'Falta la zona.' };
  // Dólares enteros con separadores de miles («390,000», «390.000», «US$ 390 000»). Un decimal,
  // un negativo o «1e6» se rechazan: quitar lo que no es dígito los volvía otro precio (revisión de Codex).
  const crudo = f.precioDesdeUsd.trim();
  if (/\d/.test(crudo) && !/^(US)?\$?\s*\d{1,3}([,. ]?\d{3})*$/i.test(crudo)) {
    return { error: 'El precio desde va en dólares enteros, por ejemplo 390,000.' };
  }
  const digitos = crudo.replace(/\D/g, '');
  const precio = Number(digitos);
  if (!digitos || !Number.isSafeInteger(precio) || precio <= 0) return { error: 'El precio desde tiene que ser un número.' };
  let inicial: number | null = null;
  if (f.inicialMinimaPct.trim()) {
    inicial = Number(f.inicialMinimaPct.trim());
    if (!Number.isInteger(inicial) || inicial < 0 || inicial > 100) return { error: 'La inicial mínima va de 0 a 100 %.' };
  }
  // La forma y que el día exista: «2026-02-30» no es una fecha (revisión de Codex).
  const dia = new Date(`${f.vigenteHasta}T00:00:00Z`);
  const fechaReal = /^\d{4}-\d{2}-\d{2}$/.test(f.vigenteHasta) && !Number.isNaN(dia.getTime()) && dia.toISOString().slice(0, 10) === f.vigenteHasta;
  if (!fechaReal) return { error: 'Falta hasta cuándo vale el precio.' };
  const enlace = f.enlace.trim();
  if (enlace && !/^https?:\/\/\S+$/i.test(enlace)) return { error: 'Pega el enlace del proyecto completo, empezando por https://' };
  return {
    datos: {
      nombre: f.nombre.trim(),
      zona: f.zona.trim(),
      precioDesdeUsd: precio,
      inicialMinimaPct: inicial,
      entrega: f.entrega.trim() || null,
      amenidades: f.amenidades.split('\n').map((a) => a.trim()).filter(Boolean),
      fotos: f.fotos,
      videos: f.videos,
      enlace: enlace || null,
      vigenteHasta: f.vigenteHasta,
      noRecomendar: f.noRecomendar,
    },
  };
}
